'use client';

/**
 * The assistant card, drawn as Main.dc.html draws it.
 *
 * WHY THE CARD IS NOT THE WIDGET. The assistant is a FLOATING panel: it renders its own trigger
 * and expands over the page. Dropping it into this card produced an empty card and a second
 * floating trigger, with and without AI-processing consent. So the page draws the card the
 * artboard specifies, and every control on it hands off to the ONE real assistant through
 * `openAssistant`. There is no second chat here: nothing on this card talks to a server.
 *
 * Every word is Main.dc.html's. The opening message is the same sentence the assistant itself
 * opens with â getRandomGreeting() builds it from AXEL_WHEREABOUTS, and the artboard shows it
 * with the first of those (âat the hot springsâ).
 */

import { useEffect, useRef, useState } from 'react';
import { openAssistant, setPreferredLevel, type AssistantLevel } from './openAssistant';

/**
 * The idle loop in the video well: Axel AI Assisted turning a feather, from the avatar product's
 * own footage.
 *
 * THIS CLIP WAS REMOVED ONCE, ON ANNETTE'S RULING (80f8ac6, 3 Aug 2026). The reason is recorded
 * in public/avatar-fillers/manifest.json: "read as finger-sniffing AT WIDGET SIZE". That ruling
 * was about the small floating widget, where the hand comes near the face in a frame about 90px
 * wide and the feather is not legible.
 *
 * It is restored HERE and nowhere else. This well is roughly 560x220 - six times the widget's
 * area - and at that size the feather is plainly a feather. The widget's own reel is unchanged
 * and stays calm-only, so the ruling still holds where it was made. If this ever reads wrong at
 * a smaller breakpoint, it goes again.
 */
const IDLE_CLIP = '/avatar-fillers/feather.mp4';
/**
 * A still of the same clip, at the frame where the feather is clearest. The well must never be
 * a black box: autoplay is refused on some phones and by some settings, reduced motion holds
 * the first frame, and a slow connection shows the poster for as long as it takes. Extracted
 * from the clip itself, so it cannot show something the video does not.
 */
const IDLE_POSTER = '/avatar-fillers/feather-poster.jpg';

export function AxelCard({ greeting }: { greeting: string }) {
  const [question, setQuestion] = useState('');
  /**
   * The reading level the visitor picked HERE, carried into the assistant when it opens.
   *
   * These two buttons had no state at all: both called `open()` with nothing, and "Standard" was
   * drawn as the selected one in every case because its colours were hard-coded. So pressing
   * Easy Read changed nothing a visitor could see, and the assistant opened at whatever level it
   * was last left at. (Annette, 22 Oct 2026: "it doesnt pass across text or settings".)
   */
  const [level, setLevel] = useState<AssistantLevel>('standard');
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reduced motion is a setting, not a preference to be overridden: hold the first frame.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      if (reduce.matches) v.pause();
      else void v.play().catch(() => undefined);
    };
    apply();
    reduce.addEventListener('change', apply);
    return () => reduce.removeEventListener('change', apply);
  }, []);

  const open = (q?: string) => openAssistant(q, level);

  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-[16px] border-[1.5px] border-ink bg-surface">
      <div className="flex items-center justify-between gap-3 border-b border-border px-[18px] py-[14px]">
        <span className="font-display text-[1.0625rem] font-semibold">Axel AI Assisted</span>
        <span className="font-mono text-chip font-normal text-muted">
          An AI, not Axel · answers for aXai
        </span>
      </div>

      <button
        type="button"
        onClick={() => open()}
        className="relative flex h-[220px] w-full items-end justify-between gap-2 overflow-hidden bg-ink p-sm text-left"
        // Distinct from the "Open Axel AI Assisted" button beside the card: two controls with
        // the SAME accessible name on one page give a screen-reader user a list with two
        // identical entries and no way to tell them apart (WCAG 2.4.6). This one says where it
        // is. (22 Oct 2026.)
        aria-label="Open Axel AI Assisted from the video"
      >
        {/* Decorative: the words beside it say what it is, and the button says what it does.
            Muted and playsInline so a phone does not take over the screen; paused to a still
            frame when the visitor has asked for reduced motion. */}
        <video
          ref={videoRef}
          // object-position keeps his face and the feather in frame: the well is far wider
          // than 16:9, so a centre crop would cut the top of his head off.
          className="absolute inset-0 h-full w-full object-cover object-[center_28%]"
          src={IDLE_CLIP}
          poster={IDLE_POSTER}
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          tabIndex={-1}
        />
        {/* A scrim, not a guess: the footage is live video, so the pixels under this line
            change. Without it the caption sat on a bright part of the frame and fell under
            contrast. The gradient is ink, the card's own ground. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink to-transparent"
        />
        <span className="relative font-mono text-chip font-normal text-paper">
          Text answers straight away · video takes a moment
        </span>
        <span className="relative rounded-chip bg-accent px-[10px] py-1 text-chip font-bold text-onAccent">
          Axel AI Assisted
        </span>
      </button>

      <div className="flex flex-col gap-3 p-[18px]">
        <p className="m-0 max-w-[85%] self-start rounded-[14px] bg-paper px-sm py-3 text-ui">
          {greeting}
        </p>

        {/* Standard / Easy Read. The assistant owns the setting; these choose which level it
            opens at, and say which one is chosen. A press no longer opens the panel on its own:
            picking a reading level is a setting, not a command to start talking. */}
        <div className="flex gap-2" role="radiogroup" aria-label="Reading mode">
          {([
            ['standard', 'Standard'],
            ['easy-read', 'Easy Read'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={level === value}
              onClick={() => {
                setLevel(value);
                // ...and tell the rest of the page, so "Open Axel AI Assisted" beside the card
                // opens at the level chosen here rather than at Standard.
                setPreferredLevel(value);
              }}
              className={`inline-flex min-h-touch items-center rounded-chip border-[1.5px] border-ink px-[14px] py-2 text-meta font-bold ${
                level === value ? 'bg-ink text-onInk' : 'bg-surface text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            open(question);
            setQuestion('');
          }}
        >
          <label htmlFor="axel-q" className="sr-only">
            Ask Axel AI Assisted
          </label>
          <input
            id="axel-q"
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about the studio…"
            className="min-h-touch min-w-0 flex-auto rounded-control border-[1.5px] border-ink bg-surface px-[14px] py-3 text-ui text-ink"
          />
          <button
            type="submit"
            className="inline-flex min-h-touch items-center rounded-control bg-ink px-[18px] py-3 text-ui font-bold text-onInk"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
